import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useForm, useWatch, type FieldErrors, type Resolver } from 'react-hook-form';
import { ChevronDown, Upload } from 'lucide-react';
import {
  IMPACTS,
  REQUEST_TYPES,
  requestSubmissionSchema,
  type ConditionalFieldSettings,
  type DuplicateCandidate,
  type DuplicateSearchInput,
  type Impact,
  type ProductCard,
  type RequestSubmissionInput,
  type RequestType,
  type TeamChoice,
} from '@product-portal/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { SelectedFiles } from '@/components/portal/selected-files';
import { journalIgnoredSuggestions, searchDuplicates, useDuplicates } from '@/hooks/use-requests';
import { ApiRequestError, ApiValidationError } from '@/lib/api';
import { IMPACT_FORM_LABELS, OTHER_PRODUCT_LABEL, TYPE_DESCRIPTIONS, TYPE_LABELS } from '@/lib/labels';
import { filesOf } from '@/lib/files';
import { cn } from '@/lib/utils';
import { DuplicatePanel } from './duplicate-panel';
import { DuplicatePrompt, type PromptChoice } from './duplicate-prompt';

/** Value of the « Autre / je ne sais pas » option: submitted as `productId: null` (ADR-003). */
const OTHER = 'other';

interface FormValues {
  type: RequestType;
  /** '' until chosen, a product id, or OTHER. */
  productChoice: string;
  /** « Équipe » (ADR-005 §6): '' when deduced. */
  teamId: string;
  title: string;
  description: string;
  impactDeclared: Impact;
  url: string;
  cmsArticleUrl: string;
  clientEmail: string;
  clientNcct: string;
}

const productIdOf = (choice: string) => (choice === OTHER || choice === '' ? null : choice);

function toSubmission(values: FormValues, teams: TeamChoice[], extra: Partial<RequestSubmissionInput>): RequestSubmissionInput {
  return {
    type: values.type,
    productId: productIdOf(values.productChoice),
    title: values.title,
    description: values.description,
    impactDeclared: values.impactDeclared,
    url: values.url,
    cmsArticleUrl: values.cmsArticleUrl,
    clientEmail: values.clientEmail,
    clientNcct: values.clientNcct,
    // Sent only when there is a choice to make; deduced by the API otherwise.
    ...(teams.length > 1 && values.teamId ? { teamId: values.teamId } : {}),
    ...extra,
  };
}

/**
 * What the duplicate search reads (ADR-009): type, product, title,
 * description and the exact signals. Null while there is nothing to search,
 * or for « Aide / accès », never a source.
 */
function searchInputOf(values: FormValues, extra: Partial<RequestSubmissionInput>): DuplicateSearchInput | null {
  if (!values.productChoice || values.type === 'help') return null;
  const exact = [values.cmsArticleUrl, values.clientEmail, values.clientNcct].some((v) => v.trim().length > 0);
  if (`${values.title} ${values.description}`.trim().length < 3 && !exact) return null;
  return {
    type: values.type,
    productId: productIdOf(values.productChoice),
    title: values.title,
    description: values.description,
    cmsArticleUrl: values.cmsArticleUrl,
    clientEmail: values.clientEmail,
    clientNcct: values.clientNcct,
    ...(extra.requesterId ? { requesterId: extra.requesterId } : {}),
  };
}

function settingsOf(product: ProductCard | undefined): ConditionalFieldSettings | null {
  return product ? { requiresCmsArticleUrl: product.requiresCmsArticleUrl, requiresClientInfo: product.requiresClientInfo } : null;
}

function fieldOf(path: readonly PropertyKey[]): keyof FormValues {
  return (path[0] === 'productId' ? 'productChoice' : path[0]) as keyof FormValues;
}

/**
 * Validation with the SAME shared Zod schema as the API (lot 2), built from the
 * settings of the selected product and from the teams of the REQUESTER that may
 * submit on it. The API validates again: this only spares a round trip.
 */
function makeResolver(products: ProductCard[], teamsFor: (productId: string | null) => TeamChoice[], extra: Partial<RequestSubmissionInput>): Resolver<FormValues> {
  return async (values) => {
    if (!values.productChoice) {
      return { values: {}, errors: { productChoice: { type: 'required', message: 'Choisissez un produit, ou « Autre / je ne sais pas ».' } } };
    }
    const product = products.find((p) => p.id === values.productChoice);
    const teams = teamsFor(productIdOf(values.productChoice));
    const result = requestSubmissionSchema(settingsOf(product), teams).safeParse(toSubmission(values, teams, extra));
    if (result.success) return { values, errors: {} };
    const errors: FieldErrors<FormValues> = {};
    for (const issue of result.error.issues) errors[fieldOf(issue.path)] ??= { type: 'validation', message: issue.message };
    return { values: {}, errors };
  };
}

function useDebounced<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export const inputClass = 'min-h-11 rounded-control border border-input bg-card px-3 font-[inherit] text-sm text-foreground';

export function Field({ id, label, required, optional, hint, error, children }: {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-bold">
        {label}
        {required && (
          <span aria-hidden="true" className="text-required">
            *
          </span>
        )}
        {optional && <span className="font-normal text-muted-foreground"> (facultatif)</span>}
      </label>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {error && (
        <span role="alert" className="text-xs font-bold text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

export const NativeSelect = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & { id: string }>(
  ({ id, children, className, ...props }, ref) => (
    <span className="relative flex">
      <select ref={ref} id={id} className={cn(inputClass, 'min-w-0 flex-1 cursor-pointer appearance-none pr-10', className)} {...props}>
        {children}
      </select>
      <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-foreground-secondary" />
    </span>
  ),
);
NativeSelect.displayName = 'NativeSelect';

export type SubmitIntent = 'create' | 'qualify';

export interface RequestFormProps {
  /** Products the REQUESTER's teams may submit on. */
  products: ProductCard[];
  /** The requester's teams that may own the request, for a product id or « Autre » (null). */
  teamsFor: (productId: string | null) => TeamChoice[];
  impactLabel: string;
  descriptionHint: string;
  /** Fields merged into the submission (screen 15: requesterId, intakeChannel). */
  extra?: Partial<RequestSubmissionInput>;
  /** Blocks rendered at the top of the form (screen 15: « Pour le compte de »). */
  before?: ReactNode;
  /** Locks the form (screen 15 before a requester is chosen). */
  locked?: boolean;
  duplicateIntro: string;
  /**
   * Action of a suggestion of the side panel. `join` validates the form, then
   * runs « M'abonner et ajouter mon contexte » (same workflow as « C'est le
   * même problème », arbitrage of 2026-10-19).
   */
  duplicateAction: (d: DuplicateCandidate, join: () => void, busy: boolean) => ReactNode;
  /** No request is created: subscription to the candidate and context added there as a public message. */
  onSameProblem: (candidate: DuplicateCandidate, input: RequestSubmissionInput, files: File[], place: 'panel' | 'prompt') => Promise<void>;
  /** Screen 16 on « Envoyer » when the best candidate reaches the threshold (screen 02 only, not 15). */
  promptOnSubmit: boolean;
  cancelTo: string;
  /** Submit buttons: each calls `submit(intent)`. */
  renderActions: (submit: (intent: SubmitIntent) => void, submitting: boolean) => ReactNode;
  onSubmit: (input: RequestSubmissionInput, files: File[], intent: SubmitIntent) => Promise<void>;
  aside?: ReactNode;
  /** Messages for fields that live outside the form (requester, channel). */
  onOuterErrors?: (issues: { path: (string | number)[]; message: string }[]) => void;
}

/**
 * The request form of screen 02, reused as is by screen 15. No priority field:
 * the priority belongs to the PO.
 */
export function RequestForm(props: RequestFormProps) {
  const { products, teamsFor, extra = {} } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const intent = useRef<SubmitIntent>('create');
  const resolver = useMemo(() => makeResolver(products, teamsFor, extra), [products, teamsFor, extra]);

  const form = useForm<FormValues>({
    resolver,
    defaultValues: {
      type: 'bug',
      productChoice: '',
      teamId: '',
      title: '',
      description: '',
      impactDeclared: 'annoying',
      url: '',
      cmsArticleUrl: '',
      clientEmail: '',
      clientNcct: '',
    },
  });
  const { register, handleSubmit, formState, setError, control, setValue } = form;
  const [type, productChoice, teamId] = useWatch({ control, name: ['type', 'productChoice', 'teamId'] });
  const watched = useWatch({ control }) as FormValues;
  const product = products.find((p) => p.id === productChoice);
  const settings = settingsOf(product);
  const teams = useMemo(() => (productChoice ? teamsFor(productIdOf(productChoice)) : []), [productChoice, teamsFor]);

  // ADR-009: the side panel searches on the whole text and the exact signals, debounced.
  // Debounced as a string: a new object on every render would restart the timer for ever.
  const searchKey = useDebounced(JSON.stringify(searchInputOf(watched, extra)));
  const searchInput = useMemo(() => JSON.parse(searchKey) as DuplicateSearchInput | null, [searchKey]);
  const duplicates = useDuplicates(searchInput);
  const suggestions = searchInput ? (duplicates.data?.items ?? []) : [];
  const [prompt, setPrompt] = useState<{ candidate: DuplicateCandidate; others: number; input: RequestSubmissionInput; search: DuplicateSearchInput } | null>(null);
  const [busy, setBusy] = useState(false);
  /** Suggestions displayed at the time of sending: journaled with their outcome by the API. */
  const shownIds = suggestions.map((d) => d.id);

  // A team that is no longer eligible for the chosen product is dropped.
  useEffect(() => {
    if (teamId && !teams.some((t) => t.id === teamId)) setValue('teamId', '');
  }, [teams, teamId, setValue]);

  // Copied before the input is reset or the drop event ends (see lib/files).
  const addFiles = (list: FileList | null) => {
    const picked = filesOf(list);
    if (picked.length > 0) setFiles((current) => [...current, ...picked]);
  };

  /** Server validation errors go back to their field; the others above the buttons. */
  async function withServerErrors(run: () => Promise<void>) {
    setServerError(null);
    setBusy(true);
    try {
      await run();
    } catch (error) {
      if (error instanceof ApiValidationError) {
        const outer: typeof error.issues = [];
        for (const issue of error.issues) {
          const field = fieldOf(issue.path);
          if (field in form.getValues()) setError(field, { type: 'server', message: issue.message });
          else outer.push(issue);
        }
        if (outer.length > 0) props.onOuterErrors?.(outer);
      } else if (error instanceof ApiRequestError) {
        setServerError(error.message);
      }
    } finally {
      setBusy(false);
    }
  }

  const submit = handleSubmit(async (values) => {
    const input = toSubmission(values, teams, extra);
    const search = searchInputOf(values, extra);
    await withServerErrors(async () => {
      if (props.promptOnSubmit && search) {
        // Fresh search on what is being sent, not on the debounced panel: screen 16 opens above the threshold.
        const result = await searchDuplicates(search);
        const candidate = result.items.find((d) => d.id === result.promptId);
        if (candidate) {
          setPrompt({ candidate, others: result.items.length - 1, input, search });
          return;
        }
      }
      await props.onSubmit({ ...input, duplicateSuggestions: { shown: shownIds } }, files, intent.current);
    });
  });

  /** « M'abonner et ajouter mon contexte » from the panel: the form must be valid. */
  const join = (candidate: DuplicateCandidate) =>
    void handleSubmit((values) =>
      withServerErrors(() =>
        props.onSameProblem(candidate, { ...toSubmission(values, teams, extra), duplicateSuggestions: { shown: shownIds } }, files, 'panel'),
      ),
    )();

  const confirmPrompt = (choice: PromptChoice) => {
    if (!prompt) return;
    const suggestions = { shown: shownIds, prompted: prompt.candidate.id };
    void withServerErrors(async () => {
      if (choice === 'same') await props.onSameProblem(prompt.candidate, { ...prompt.input, duplicateSuggestions: suggestions }, files, 'prompt');
      else await props.onSubmit({ ...prompt.input, duplicateSuggestions: suggestions }, files, intent.current);
      setPrompt(null);
    });
  };

  const backToForm = () => {
    if (prompt) journalIgnoredSuggestions({ ...prompt.search, candidateIds: [prompt.candidate.id], place: 'prompt' });
    setPrompt(null);
  };

  return (
    <div className="flex flex-wrap items-start gap-6">
      <form
        noValidate
        onSubmit={(event) => void submit(event)}
        className="flex min-w-0 flex-[999_1_560px] flex-col gap-[22px] rounded-card border border-border bg-card p-6"
      >
        {props.before}
        <fieldset disabled={props.locked} className="m-0 flex min-w-0 flex-col gap-[22px] border-0 p-0 disabled:opacity-60">
          <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
            <legend className="mb-2.5 p-0 text-sm font-bold">Type de demande</legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {REQUEST_TYPES.map((t) => (
                <label
                  key={t}
                  className={cn(
                    'flex min-h-11 cursor-pointer flex-col gap-1 rounded-control px-4 py-3.5',
                    type === t ? 'border-2 border-primary bg-accent' : 'border border-border bg-card',
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-bold">
                    <input type="radio" value={t} className="m-0" {...register('type')} /> {TYPE_LABELS[t]}
                  </span>
                  <span className="text-xs text-muted-foreground">{TYPE_DESCRIPTIONS[t]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="produit" label="Produit concerné" error={formState.errors.productChoice?.message}>
              <NativeSelect id="produit" {...register('productChoice')}>
                {/* No preselected product: a default would push a hesitant requester to file it at random (ADR-003). */}
                <option value="" disabled>
                  Choisissez un produit
                </option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                <option value={OTHER}>{OTHER_PRODUCT_LABEL}</option>
              </NativeSelect>
            </Field>
            {/* ADR-005 §6: only when several of the requester's teams may submit on the product. */}
            {teams.length > 1 ? (
              <Field id="equipe" label="Équipe" required hint="La demande sera suivie dans la vue de cette équipe." error={formState.errors.teamId?.message}>
                <NativeSelect id="equipe" aria-required="true" {...register('teamId')}>
                  <option value="" disabled>
                    Choisissez une équipe
                  </option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            ) : (
              <div aria-hidden="true" />
            )}
          </div>

          <Field id="titre" label="Titre" error={formState.errors.title?.message}>
            <input id="titre" type="text" className={inputClass} {...register('title')} />
          </Field>

          <Field id="desc" label="Description" hint={props.descriptionHint} error={formState.errors.description?.message}>
            <textarea id="desc" rows={6} className={cn(inputClass, 'resize-y py-3 leading-normal')} {...register('description')} />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {settings?.requiresCmsArticleUrl ? (
              <Field
                id="url-cms"
                label="URL de l'article dans le CMS"
                required
                hint="Copiez l'adresse de l'article depuis la barre du navigateur, dans le CMS."
                error={formState.errors.cmsArticleUrl?.message}
              >
                <input id="url-cms" type="url" placeholder="https://…" aria-required="true" className={inputClass} {...register('cmsArticleUrl')} />
              </Field>
            ) : (
              <Field id="url" label="URL concernée" optional error={formState.errors.url?.message}>
                <input id="url" type="url" placeholder="https://…" className={inputClass} {...register('url')} />
              </Field>
            )}
            <Field id="impact" label={props.impactLabel}>
              <NativeSelect id="impact" {...register('impactDeclared')}>
                {IMPACTS.map((i) => (
                  <option key={i} value={i}>
                    {IMPACT_FORM_LABELS[i]}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>

          {settings?.requiresClientInfo && (
            <div className="flex flex-col gap-3 rounded-control border border-border bg-surface-subtle p-4">
              <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-foreground-secondary">Client concerné</span>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field id="email-client" label="Email du client" required error={formState.errors.clientEmail?.message}>
                  <input id="email-client" type="email" placeholder="client@exemple.fr" aria-required="true" className={inputClass} {...register('clientEmail')} />
                </Field>
                {/* Free text: the NCCT has no fixed form, only its presence is checked. */}
                <Field id="ncct" label="NCCT du client" required error={formState.errors.clientNcct?.message}>
                  <input id="ncct" type="text" aria-required="true" className={inputClass} {...register('clientNcct')} />
                </Field>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold">Pièces jointes</span>
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                addFiles(event.dataTransfer.files);
              }}
              className={cn(
                'flex flex-wrap items-center gap-3.5 rounded-control border border-dashed bg-surface-subtle p-[18px]',
                dragging ? 'border-primary' : 'border-input',
              )}
            >
              <Upload aria-hidden="true" className="size-5 text-foreground-secondary" />
              <span className="flex-1 text-sm text-foreground-secondary">Glissez une capture d'écran ou un fichier ici</span>
              <Button type="button" variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
                Parcourir
              </Button>
              <input
                ref={fileInput}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </div>
            <SelectedFiles files={files} onRemove={(index) => setFiles((current) => current.filter((_, i) => i !== index))} />
          </div>
        </fieldset>

        {serverError && (
          <p role="alert" className="m-0 rounded-control border border-warning-border bg-warning px-3.5 py-2.5 text-sm text-warning-foreground">
            {serverError}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <Link
            to={props.cancelTo}
            onClick={() => {
              if (searchInput && shownIds.length > 0) journalIgnoredSuggestions({ ...searchInput, candidateIds: shownIds, place: 'panel' });
            }}
            className={cn(buttonVariants({ variant: 'secondary' }), 'min-h-11 text-foreground no-underline hover:text-foreground')}
          >
            Annuler
          </Link>
          <div className="flex flex-wrap gap-2.5">
            {props.renderActions((next) => {
              intent.current = next;
              void submit();
            }, formState.isSubmitting || busy)}
          </div>
        </div>
      </form>

      <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-6">
        <DuplicatePanel
          items={suggestions}
          scopeLabel={product ? `du ${product.name}` : 'de tous les produits'}
          intro={props.duplicateIntro}
          action={(d) => props.duplicateAction(d, () => join(d), busy)}
        />
        {props.aside}
      </aside>

      {prompt && (
        <DuplicatePrompt
          candidate={prompt.candidate}
          others={prompt.others}
          draft={{ title: prompt.input.title, productName: product?.name ?? OTHER_PRODUCT_LABEL, type: prompt.input.type, impact: prompt.input.impactDeclared }}
          sameProduct={prompt.input.productId !== null}
          busy={busy}
          onConfirm={confirmPrompt}
          onBack={backToForm}
        />
      )}
    </div>
  );
}
